import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  Typography,
  useTheme,
} from '@mui/material';
import { useAppSelector } from '../../../../app/hooks';
import {
  isBlueberryFeature,
  isBlueberryFlowerFeature,
} from '../../../../services/data/impl/cube/cubeUtils';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import LoadingSpinner from '../../../commonUI/LoadingSpinner';
import { flexCenter } from '../../../commonUI/cssClasses';
import { MeasurementTypes, useUnitSymbol } from '../../../commonUtils';
import { roundFractional } from '../../../ranches/ranchUtils';
import { selectIsLoadingCustomerSummary } from '../../../ranches/ranchesSlice';
import {
  getBlueberryColorsDistributionByRawData,
  useBlueberriesMeanStdDev,
} from '../../../scanDataApi/hooks/useBlueberryDistributionByClass';
import { selectCurrBlueberryClass } from '../../../select/selectSlice';
import { selectPointsHeatmapLoading } from '../../mapSlice';
type ColorBoxProps = {
  color: string;
};

const ColorBox: React.FC<ColorBoxProps> = ({ color }) => (
  <div
    style={{
      background: color,
      minHeight: '0.8rem',
      minWidth: '0.8rem',
      borderRadius: '4px',
    }}
  />
);

const BlueberryLegend: React.FC = () => {
  const theme = useTheme();
  const currBlueberryClass = useAppSelector(selectCurrBlueberryClass);
  const pointsHeatmapLoading = useAppSelector(selectPointsHeatmapLoading);
  // const currMeasure = useAppSelector(selectCurrMeasure);
  const isLoadingCustomerSummary = useAppSelector(
    selectIsLoadingCustomerSummary
  );
  const meterOrFeetUnitSymbol = useUnitSymbol(MeasurementTypes.meterOrFeet);

  const {
    data: stdDevMeanData,
    isLoading: stdDevLoading,
    error: stdDevError,
  } = useBlueberriesMeanStdDev();

  const { t } = useTranslation();
  const ranges =
    !!stdDevMeanData?.std_dev && !!stdDevMeanData?.mean
      ? getBlueberryColorsDistributionByRawData(
          stdDevMeanData.std_dev,
          stdDevMeanData.mean,
          currBlueberryClass
        )
      : null;

  if (
    stdDevLoading ||
    stdDevError ||
    pointsHeatmapLoading ||
    isLoadingCustomerSummary
  ) {
    return (
      <Box
        sx={{
          width: '100%',
          ...flexCenter,
          flexDirection: 'column',
          p: '0.3rem',
        }}
      >
        <LoadingSpinner />
      </Box>
    );
  }

  if (!ranges) {
    return (
      <Box
        sx={{
          width: '100%',
          ...flexCenter,
          flexDirection: 'column',
          p: '0.3rem',
        }}
      >
        <Typography sx={{ color: (theme) => theme.colors.common.white }}>
          {t('No Data', { ns: 'common' })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        ...flexCenter,
        flexDirection: 'column',
        p: '0.3rem',
      }}
    >
      <Typography
        sx={{
          fontSize: '1rem',
          color: theme.colors.common.white,
        }}
        gutterBottom
      >
        {`${t(
          isBlueberryFlowerFeature(currBlueberryClass)
            ? 'Flowers'
            : isBlueberryFeature(currBlueberryClass)
            ? 'Berries'
            : 'Detections',
          {
            ns: 'common',
          }
        )} / ${meterOrFeetUnitSymbol}`}
      </Typography>
      <List dense sx={{ p: 0 }}>
        {ranges.map((el, idx) => (
          <ListItem key={idx} sx={{ p: 0 }}>
            <ListItemIcon sx={{ minWidth: '2rem' }}>
              <ColorBox color={el[2] as string} />
            </ListItemIcon>
            <Typography
              sx={{
                fontSize: '1rem',
                color: theme.colors.common.white,
              }}
            >
              {idx !== ranges.length - 1
                ? `${roundFractional(el[0], 0)} - ${roundFractional(el[1], 0)}`
                : `> ${roundFractional(el[0], 0)}`}
            </Typography>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default BlueberryLegend;
